import { useState, useEffect, useMemo } from "react";
import type { GroupView, Member } from "@/services/types";
import { groupService, memberService } from "@/services";
import { useToast } from "@/context/ToastContext";
import { Modal } from "@/components/base/Modal";
import { Button } from "@/components/base/Button";
import { TextField } from "@/components/base/TextField";
import { Alert } from "@/components/base/Alert";
import { Avatar } from "@/components/base/Avatar";
import { Badge } from "@/components/base/Badge";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";

interface AddMemberModalProps {
  open: boolean;
  group: GroupView;
  onClose: () => void;
  onSuccess: () => void;
}

export function AddMemberModal({ open, group, onClose, onSuccess }: AddMemberModalProps) {
  const toast = useToast();

  const [allMembers, setAllMembers] = useState<Member[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMembers, setSelectedMembers] = useState<Member[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;

    let active = true;
    setError(null);
    setSelectedMembers([]);
    setSearchQuery("");
    setLoadingMembers(true);

    memberService
      .listMembers()
      .then((members) => {
        if (!active) return;
        setAllMembers(members);
      })
      .catch(() => {})
      .finally(() => {
        if (active) setLoadingMembers(false);
      });

    return () => {
      active = false;
    };
  }, [open]);

  // Current active members in this group cannot be added
  const activeEnrolledMemberIds = useMemo(() => {
    return new Set(
      group.tickets
        .filter((t) => t.memberId && t.member)
        .map((t) => t.memberId)
    );
  }, [group.tickets]);

  // Currently selected member IDs in this modal
  const selectedMemberIds = useMemo(() => {
    return new Set(selectedMembers.map((m) => m.id));
  }, [selectedMembers]);

  // Filter available candidate members
  const candidateMembers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return allMembers
      .filter((m) => {
        if (activeEnrolledMemberIds.has(m.id)) return false;
        if (m.status === "blacklisted") return false;
        if (!q) return true;
        return (
          m.fullName.toLowerCase().includes(q) ||
          m.memberCode.toLowerCase().includes(q) ||
          m.primaryPhone.includes(q)
        );
      })
      .slice(0, 15);
  }, [allMembers, activeEnrolledMemberIds, searchQuery]);

  const countToAdd = selectedMembers.length;
  const newTotalMembers = group.memberCount + countToAdd;
  const newPoolPrice = newTotalMembers * group.monthlyInstallment;
  const firstNewTicket = group.memberCount + 1;
  const lastNewTicket = group.memberCount + countToAdd;

  const handleToggleMember = (member: Member) => {
    if (selectedMemberIds.has(member.id)) {
      setSelectedMembers((prev) => prev.filter((m) => m.id !== member.id));
    } else {
      setSelectedMembers((prev) => [...prev, member]);
    }
  };

  const handleRemoveMember = (memberId: string) => {
    setSelectedMembers((prev) => prev.filter((m) => m.id !== memberId));
  };

  const handleSubmit = async () => {
    if (selectedMembers.length === 0) {
      setError("Please select at least one member to add to this group.");
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const memberIds = selectedMembers.map((m) => m.id);
      await groupService.expandAndAddMember(group.id, memberIds);
      toast.success(
        `${selectedMembers.length} member(s) added successfully`,
        `Capacity expanded to ${newTotalMembers} members. Pool price updated to ₹${newPoolPrice.toLocaleString("en-IN")}.`
      );
      onClose();
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add members to group.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
            <i className="ri-user-add-line text-lg" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-foreground-900">
              Add Members & Expand Group Capacity
            </h2>
            <p className="text-xs text-foreground-500">
              Add one or multiple members, update pool price, and generate new auction cycles.
            </p>
          </div>
        </div>
      }
      footer={
        <div className="flex w-full items-center justify-between gap-3">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={selectedMembers.length === 0 || submitting}
            icon={submitting ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-check-line" />}
          >
            {submitting
              ? "Adding Members…"
              : selectedMembers.length === 0
              ? "Select Members to Add"
              : selectedMembers.length === 1
              ? `Confirm & Add Ticket #${firstNewTicket}`
              : `Confirm & Add ${selectedMembers.length} Members (Tickets #${firstNewTicket} - #${lastNewTicket})`}
          </Button>
        </div>
      }
    >
      <div className="space-y-4 py-1">
        {error ? (
          <Alert variant="danger" title="Addition Error" onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : null}

        {/* Policy Notice */}
        <div className="rounded-lg border border-primary-200 bg-primary-50/60 p-3 text-xs text-primary-900 space-y-1">
          <div className="flex items-center gap-1.5 font-semibold text-primary-950">
            <i className="ri-shield-check-line text-primary-700 text-sm" />
            <span>Policy: 0 Cycles Conducted</span>
          </div>
          <p className="text-primary-800 text-[11px] leading-relaxed">
            Because no auctions or cycles have been completed yet, adding members automatically expands the group duration, increases the gross pool price, and generates the corresponding auction cycles.
          </p>
        </div>

        {/* Group Economics Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 rounded-lg border border-background-200 bg-background-100/50 p-3">
          <div>
            <div className="text-[11px] text-foreground-500">Total Members</div>
            <div className="tnum mt-0.5 text-sm font-semibold text-foreground-900">
              {group.memberCount}
              {countToAdd > 0 ? (
                <>
                  <i className="ri-arrow-right-line text-xs text-primary-600 mx-1" />
                  <span className="text-primary-700">{newTotalMembers}</span>
                </>
              ) : null}
            </div>
          </div>
          <div>
            <div className="text-[11px] text-foreground-500">Pool Price (Chit Value)</div>
            <div className="tnum mt-0.5 text-sm font-semibold text-foreground-900">
              {countToAdd > 0 ? (
                <CurrencyDisplay amount={newPoolPrice} hideSymbol className="text-primary-700" />
              ) : (
                <CurrencyDisplay amount={group.chitValue} hideSymbol />
              )}
            </div>
          </div>
          <div>
            <div className="text-[11px] text-foreground-500">Duration</div>
            <div className="tnum mt-0.5 text-sm font-semibold text-foreground-900">
              {countToAdd > 0 ? `${newTotalMembers} months` : `${group.memberCount} months`}
            </div>
          </div>
          <div>
            <div className="text-[11px] text-foreground-500">Assigned Tickets</div>
            <div className="tnum mt-0.5 text-sm font-semibold text-primary-700">
              {countToAdd === 0
                ? "—"
                : countToAdd === 1
                ? `Ticket #${firstNewTicket}`
                : `Tickets #${firstNewTicket} - #${lastNewTicket}`}
            </div>
          </div>
        </div>

        {/* Selected Members Basket */}
        {selectedMembers.length > 0 ? (
          <div className="rounded-lg border border-primary-200 bg-primary-50/40 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-primary-950">
                Selected Members to Add ({selectedMembers.length})
              </span>
              <button
                type="button"
                onClick={() => setSelectedMembers([])}
                className="text-[11px] font-medium text-foreground-500 hover:text-danger-600 cursor-pointer"
              >
                Clear all
              </button>
            </div>
            <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-1">
              {selectedMembers.map((m, idx) => (
                <div
                  key={m.id}
                  className="flex items-center gap-2 rounded-full border border-primary-200 bg-white py-1 pl-2 pr-2.5 shadow-xs"
                >
                  <Avatar name={m.fullName} size="xs" />
                  <span className="text-xs font-medium text-foreground-900">{m.fullName}</span>
                  <span className="tnum rounded bg-primary-100 px-1 text-[10px] font-semibold text-primary-700">
                    #{group.memberCount + 1 + idx}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleRemoveMember(m.id)}
                    className="ml-0.5 flex h-4 w-4 items-center justify-center rounded-full text-foreground-400 hover:bg-danger-50 hover:text-danger-600"
                    title="Remove"
                  >
                    <i className="ri-close-line text-xs" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {/* Search and Candidate Members List */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="block text-xs font-semibold text-foreground-700">
              Search & Select Candidates
            </label>
            <span className="text-[11px] text-foreground-400">Click to add or remove members</span>
          </div>

          <TextField
            placeholder="Search candidate by name, code, or phone…"
            leftIcon="ri-search-line"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus
          />

          <div className="max-h-52 overflow-y-auto rounded-lg border border-background-200 divide-y divide-background-200 bg-background-50">
            {loadingMembers ? (
              <div className="p-4 text-center text-xs text-foreground-400">Loading members…</div>
            ) : candidateMembers.length === 0 ? (
              <div className="p-4 text-center text-xs text-foreground-400">
                {searchQuery ? "No eligible members found matching your search." : "No available candidate members."}
              </div>
            ) : (
              candidateMembers.map((m) => {
                const isSelected = selectedMemberIds.has(m.id);
                return (
                  <div
                    key={m.id}
                    onClick={() => handleToggleMember(m)}
                    className={`flex items-center justify-between p-2.5 cursor-pointer transition-colors ${
                      isSelected ? "bg-primary-50/80 hover:bg-primary-100/70" : "hover:bg-background-100"
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <Avatar name={m.fullName} size="sm" />
                      <div>
                        <div className="text-xs font-medium text-foreground-900">{m.fullName}</div>
                        <div className="text-[11px] text-foreground-500">
                          {m.memberCode} · {m.primaryPhone}
                        </div>
                      </div>
                    </div>
                    {isSelected ? (
                      <span className="inline-flex items-center gap-1 rounded bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-800">
                        <i className="ri-check-line" /> Added
                      </span>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleMember(m);
                        }}
                      >
                        <i className="ri-add-line mr-1" /> Add
                      </Button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
